import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _isString from "lodash/isString";
import _isNumber from "lodash/isNumber";
import React, { useEffect } from 'react';
import { Box } from "../Box";
import { theme } from "../../theme";
import styled, { css } from 'styled-components';
import { eases } from "../../motion/eases";
import { baseTextStyles } from "../Text/Text";
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { usePrevious } from "../../hooks";
export var Badge = React.forwardRef(function (_ref, ref) {
  var borderColor = _ref.borderColor,
      _ref$borderSize = _ref.borderSize,
      borderSize = _ref$borderSize === void 0 ? 2 : _ref$borderSize,
      children = _ref.children,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? 'transparent' : _ref$backgroundColor,
      _ref$isShowing = _ref.isShowing,
      isShowing = _ref$isShowing === void 0 ? false : _ref$isShowing,
      _ref$overlap = _ref.overlap,
      overlap = _ref$overlap === void 0 ? 'rounded' : _ref$overlap,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom-right' : _ref$placement,
      _ref$shouldAnimateOnU = _ref.shouldAnimateOnUpdate,
      shouldAnimateOnUpdate = _ref$shouldAnimateOnU === void 0 ? true : _ref$shouldAnimateOnU,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 8 : _ref$size,
      label = _ref.label,
      content = _ref.content,
      other = _objectWithoutProperties(_ref, ["borderColor", "borderSize", "children", "backgroundColor", "isShowing", "overlap", "placement", "shouldAnimateOnUpdate", "size", "label", "content"]);

  var hasTextContent = _isString(content) || _isNumber(content);

  var wasShowing = usePrevious(isShowing);
  var controls = useAnimation();
  var prevContent = usePrevious(content);
  useEffect(function () {
    if (shouldAnimateOnUpdate && _isNumber(content) && _isNumber(prevContent) && wasShowing && isShowing && content > prevContent) {
      controls.start({
        scale: 1,
        transition: {
          type: 'spring',
          stiffness: 700,
          damping: 80,
          velocity: 10
        }
      });
    }
  }, [content, controls, hasTextContent, isShowing, prevContent, shouldAnimateOnUpdate, wasShowing]);
  var formattedContent = /*#__PURE__*/React.createElement(AnimatePresence, {
    initial: false
  }, isShowing && /*#__PURE__*/React.createElement(StyledBadge, _extends({
    style: children && offsets[placement],
    initial: {
      opacity: 0,
      scale: 0
    },
    animate: {
      opacity: 1,
      scale: 0.999999,
      transition: {
        opacity: {
          duration: 0.2,
          ease: eases.primary
        },
        scale: {
          type: 'spring',
          stiffness: 281,
          damping: 22
        }
      }
    },
    exit: {
      opacity: 0,
      scale: 0,
      transition: {
        opacity: {
          duration: 0.2,
          ease: eases.primary
        },
        scale: {
          duration: 0.2,
          ease: eases.primary
        }
      }
    },
    ref: ref,
    "data-testid": label,
    "aria-label": label,
    $placement: children ? placement : undefined,
    $overlap: overlap
  }, other), /*#__PURE__*/React.createElement(StyledBadgeContent, {
    animate: controls,
    $borderColor: borderColor,
    $borderSize: borderSize,
    $hasText: hasTextContent,
    $size: !hasTextContent && size,
    $hasContent: !!content,
    $backgroundColor: backgroundColor
  }, hasTextContent ? /*#__PURE__*/React.createElement(BadgeText, null, content) : content)));

  if (children) {
    return /*#__PURE__*/React.createElement(Box, {
      position: "relative",
      display: "inline-block"
    }, children, formattedContent);
  }

  return formattedContent;
});
export var StyledBadge = styled(motion.span).withConfig({
  displayName: "Badge__StyledBadge",
  componentId: "vapor__sc-1kgdxgi-0"
})(["display:inline-block;user-select:none;", ";"], badgePlacement);
var StyledBadgeContent = styled(motion.div).withConfig({
  displayName: "Badge__StyledBadgeContent",
  componentId: "vapor__sc-1kgdxgi-1"
})(["box-shadow:", ";width:", ";height:", ";background-color:", ";border-radius:16px;color:", ";will-change:transform;", ""], badgeBorder, function (p) {
  return p.$size ? "".concat(p.$size, "px") : 'auto';
}, function (p) {
  return p.$size ? "".concat(p.$size, "px") : 'auto';
}, function (p) {
  return theme.colors.palette[p.$backgroundColor];
}, theme.colors.palette.grey100, function (p) {
  return p.$hasText && "\n    min-width: ".concat(theme.spaces[16], ";\n    min-height: ").concat(theme.spaces[16], ";\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    padding: 0 ").concat(theme.spaces[4], ";\n  ");
});
/**
 * Render a badge border (as a drop-shadow)
 */

function badgeBorder(_ref2) {
  var $borderSize = _ref2.$borderSize,
      $borderColor = _ref2.$borderColor;
  return $borderColor && $borderSize ? "0 0 0 ".concat($borderSize, "px ").concat(theme.colors.palette[$borderColor]) : 'none';
}

var offsets = {
  'top-left': {
    x: '-50%',
    y: '-50%'
  },
  'top-right': {
    x: '50%',
    y: '-50%'
  },
  'bottom-left': {
    x: '-50%',
    y: '50%'
  },
  'bottom-right': {
    x: '50%',
    y: '50%'
  }
};

function badgePlacement(_ref3) {
  var $placement = _ref3.$placement,
      _ref3$$overlap = _ref3.$overlap,
      $overlap = _ref3$$overlap === void 0 ? 'rounded' : _ref3$$overlap;
  var isCircleOverlap = $overlap === 'rounded';

  switch ($placement) {
    case 'top-left':
      return isCircleOverlap ? css(["position:absolute;top:14%;left:14%;"]) : css(["position:absolute;top:0;left:0;"]);

    case 'top-right':
      return isCircleOverlap ? css(["position:absolute;top:14%;right:14%;"]) : css(["position:absolute;top:0;right:0;"]);

    case 'bottom-left':
      return isCircleOverlap ? css(["position:absolute;bottom:14%;left:14%;"]) : css(["position:absolute;bottom:0;left:0;"]);

    case 'bottom-right':
      return isCircleOverlap ? css(["position:absolute;bottom:14%;right:14%;"]) : css(["position:absolute;bottom:0;right:0;"]);

    default:
      return css([""]);
  }
} // These values are generated via capsize. We are likely
// to eventually use this design-system wide, but until
// then I'm embedding directly.
// https://seek-oss.github.io/capsize/


export var BadgeText = styled.div.withConfig({
  displayName: "Badge__BadgeText",
  componentId: "vapor__sc-1kgdxgi-2"
})(["", ";font-size:11px;line-height:8px;padding:0.05px 0;&:before{content:'';margin-top:-0.0045em;display:block;height:0;}&:after{content:'';margin-bottom:-0.0045em;display:block;height:0;}"], baseTextStyles);